// src/pages/Home.jsx
import React, { useEffect, useRef, useState } from "react";
import { ReactTyped } from "react-typed";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import GLOBE from "vanta/dist/vanta.globe.min.js";
import me from "../assets/me.jpg";

const phrases = [
  "你好! Hello! Hola! I'm Christian Lira (李浩峰).",
  "Data without infrastructure is just noise.",
  "I build the systems that turn that noise into decisions.",
  "Analytics Engineer · ELT pipelines · Data platforms · BI delivery.",
  "SQL · Python · R · Power BI · Microsoft Fabric · DataBricks",
  "Rebuilt Finance Operations data infrastructure at Tesla.",
  "Built Orpheus, an economic intelligence platform from scratch.",
  "Fluent in English, Español, and 中文 (HSK4).",
  "Founder · Engineer · Perpetually curious.",
  "Targeting OMSCS @ GaTech, the journey continues.",
  "Scroll down. Let the work speak. ↓",
];

export default function Home() {
  const navigate = useNavigate();
  const bgRef = useRef(null);
  const vantaRef = useRef(null);

  // Tooltip that appears after 25 seconds
  const [showArrowHint, setShowArrowHint] = useState(false);

  useEffect(() => {
    if (!bgRef.current) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;

    vantaRef.current = GLOBE({
      el: bgRef.current,
      THREE,
      mouseControls: true,
      touchControls: true,
      gyroControls: false,
      minHeight: 200.0,
      minWidth: 200.0,
      scale: 1.0,
      scaleMobile: 1.0,
      color: 0x62a5c9,
      backgroundColor: 0x10191c,
    });

    return () => vantaRef.current?.destroy?.();
  }, []);

useEffect(() => {
  let intervalId;

  // First delay: wait 25 seconds
  const startTimer = setTimeout(() => {
    setShowArrowHint(true);

    // Then toggle every 15 seconds
    intervalId = setInterval(() => {
      setShowArrowHint((prev) => !prev);
    }, 15000);
  }, 25000);

  return () => {
    clearTimeout(startTimer);
    if (intervalId) clearInterval(intervalId);
  };
}, []);


  return (
    <section
      style={{
        position: "relative",
        width: "100%",
        height: "102vh",
        marginTop: "calc(var(--nav-h) * -1)",
        overflow: "hidden",
        zIndex: 0,
      }}
    >
      {/* Full-bleed Home background */}
      <div
        ref={bgRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: "calc(var(--nav-h) + 2rem)",
          paddingLeft: "1.5rem",
          paddingRight: "1.5rem",
          paddingBottom: "5.5rem", // extra room for the arrow outside the card
          boxSizing: "border-box",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 1100,
            display: "flex",
            gap: "2rem",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            background: "rgba(0,0,0,0.45)",
            borderRadius: 16,
            padding: "2rem",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            backdropFilter: "blur(6px)",
          }}
        >
          <img
            src={me}
            alt="Christian Lira"
            style={{
              width: "clamp(140px, 18vw, 220px)",
              height: "clamp(140px, 18vw, 220px)",
              borderRadius: "50%",
              objectFit: "cover",
              border: "3px solid #0a1e23",
            }}
          />

          <div style={{ maxWidth: 560, textAlign: "center" }}>
            <h1
              style={{
                margin: 0,
                marginBottom: "0.85rem", // more breathing room
                fontSize: "clamp(1.6rem, 3vw, 2.6rem)",
                lineHeight: 1.2,
                color: "#fff",
              }}
            >
              Hello there, I’m Christian
            </h1>

            <p
              style={{
                margin: 0,
                marginBottom: "0.65rem", // space between paragraph and credibility line
                color: "rgba(255,255,255,0.78)",
                lineHeight: 1.65,
              }}
            >
              Analytics Engineering · Data Science · Data Engineering
            </p>

            <p
              style={{
                margin: 0,
                marginBottom: "1.05rem",
                color: "rgba(255,255,255,0.65)",
                fontSize: "0.95rem",
                lineHeight: 1.6,
              }}
            >
              Currently building <strong style={{ color: "#fff" }}>Orpheus Analytics</strong> · Previously: <strong style={{ color: "#fff" }}>Tesla</strong> Finance Operations · Trilingual (EN/ES/中文)
            </p>

            <div style={{ marginBottom: "1.15rem" }}>
              <ReactTyped
                strings={phrases}
                typeSpeed={55}
                backSpeed={25}
                backDelay={1600}
                loop
                style={{
                  fontSize: "clamp(1.05rem, 2.2vw, 1.45rem)",
                  color: "#62a5c9",
                  fontWeight: 500,
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
              <button
                onClick={() => navigate("/resume")}
                style={{
                  padding: "0.75rem 1.1rem",
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "linear-gradient(180deg, #10a0d0 0%, #0e8cb6 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Resume
              </button>

              <button
                onClick={() => navigate("/contact")}
                style={{
                  padding: "0.75rem 1rem",
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "transparent",
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Contact
              </button>

              <button
                onClick={() => navigate("/about")}
                style={{
                  padding: "0.75rem 1rem",
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "transparent",
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                More About Me
              </button>
            </div>
          </div>
        </div>

        {/* Arrow OUTSIDE the card, near bottom of page */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            bottom: "1.6rem",
            transform: "translateX(-50%)",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.6rem",
          }}
        >
          {/* Tooltip bubble that appears after 25s */}
          <div
            style={{
              opacity: showArrowHint ? 1 : 0,
              transform: showArrowHint ? "translateY(0px)" : "translateY(8px)",
              transition: "opacity 500ms ease, transform 500ms ease",
              pointerEvents: "none",
              padding: "0.55rem 0.8rem",
              borderRadius: 999,
              background: "rgba(0,0,0,0.55)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "rgba(255,255,255,0.9)",
              fontSize: "0.9rem",
              backdropFilter: "blur(6px)",
              boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
              whiteSpace: "nowrap",
            }}
          >
            Explore my projects!
          </div>

          <button
            onClick={() => navigate("/projects")}
            aria-label="Go to projects"
            style={{
              width: 52,
              height: 52,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.9)",
              cursor: "pointer",
              fontSize: "1.2rem",
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 180ms ease, background 180ms ease",
              // subtle idle bounce (doesn't depend on CSS files)
              animation:
                window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
                  ? "none"
                  : "homeArrowBounce 1.8s ease-in-out infinite",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.background = "rgba(255,255,255,0.10)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px)";
              e.currentTarget.style.background = "rgba(255,255,255,0.06)";
            }}
          >
            ↓
          </button>
        </div>

        {/* Keyframes for bounce (scoped via global style tag) */}
        <style>
          {`
            @keyframes homeArrowBounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-6px); }
            }
          `}
        </style>
      </div>
    </section>
  );
}









